import React, { useContext } from "react";
import { devConsoleLog } from "../devConsoleLog";
import { ProcessContext } from "../../contexts/ProcessContext";
import { developOptions } from "../../config/developOptions";

export function useStepHandler(submitModule, fakeFormsData) {
  const {
    state: { currentModuleObject },
    dispatch,
  } = useContext(ProcessContext);
  const [progress, setProgress] = React.useState([]); //ej: [[{ step: 0, group: "A" }]]
  const [groupsDictionary, setGroupsDictionary] = React.useState({});
  const initialStepValues = developOptions.USE_FAKE_FORMS_AS_DEFAULT_VALUES
    ? fakeFormsData
    : {};
  const [allStepValues, setAllStepValues] = React.useState(initialStepValues);
  const onBack = () => {
    const newProgress = clone(progress);
    newProgress.pop();
    updateScreenPathInProcessContext(newProgress);
    setProgress(newProgress);
  };

  const existsStepInGroupArray = (step, groupToCheck) => {
    return groupsDictionary[groupToCheck][step];
  };

  const stepInGroupMustShow = (step, groupToCheck) => {
    return groupsDictionary[groupToCheck][step].show(allStepValues);
  };

  const getNextVisibleStepFromPositionPoint = ({ step, group }) => {
    if (!existsStepInGroupArray(step, group)) {
      return null;
    }
    if (stepInGroupMustShow(step, group)) {
      return step;
    } else {
      return getNextVisibleStepFromPositionPoint({ step: step + 1, group });
    }
  };

  const blindlySumAStepToProgressPosition = (progressPosition) => {
    const newProgressPosition = clone(progressPosition);
    const newLastPositionPoint = getLastArrayElement(newProgressPosition);
    newLastPositionPoint.step = newLastPositionPoint.step + 1;
    newProgressPosition.pop();
    newProgressPosition.push(newLastPositionPoint);
    return newProgressPosition;
  };

  const updateProgressPositionLastPointStepToNextVisibleStepOrPopProgressPosition =
    (progressPosition) => {
      if (progressPosition.length) {
        const lastPositionPoint = progressPosition.pop();
        const nextStep = getNextVisibleStepFromPositionPoint(lastPositionPoint);
        if (nextStep === null) {
          if (progressPosition.length) {
            return null;
          } else {
            const nextProgressPosition =
              blindlySumAStepToProgressPosition(progressPosition);
            return updateProgressPositionLastPointStepToNextVisibleStepOrPopProgressPosition(
              nextProgressPosition
            );
          }
        } else {
          progressPosition.push({
            group: lastPositionPoint.group,
            step: nextStep,
          });
          return progressPosition;
        }
      }
      return null;
    };

  const getValidProgressPosition = (progressPosition) => {
    return updateProgressPositionLastPointStepToNextVisibleStepOrPopProgressPosition(
      progressPosition
    );
  };

  const getValuesOfCompletedSteps = () => {
    let neededValues = {};
    for (const item in progress) {
      const positionPoint = getLastArrayElement(progress[item]);
      const id = groupsDictionary[positionPoint.group][positionPoint.step].id;
      if (allStepValues[id]) {
        neededValues = { ...neededValues, ...allStepValues[id] };
      }
    }
    return neededValues;
  };

  const getLastArrayElement = (array) => {
    return array[array.length - 1];
  };

  const getGroupMatchingPositionPointInLastProgressPosition = (group) => {
    if (!progress) return undefined;
    const lastProgressArrayItem = getLastArrayElement(progress);
    if (lastProgressArrayItem) {
      return lastProgressArrayItem.find((el) => el.group === group);
    }
    return undefined;
  };

  const clone = (nodesArray) => {
    return JSON.parse(JSON.stringify(nodesArray));
  };

  const getStepData = (stepId) => {
    if (!stepId) {
      const lastProgressPosition = getLastArrayElement(progress);
      const lastPositionPoint = getLastArrayElement(lastProgressPosition);
      const lastProgressPointStepId =
        groupsDictionary[lastPositionPoint.group]?.[lastPositionPoint.step]?.id;
      return allStepValues[lastProgressPointStepId];
    }
    return allStepValues[stepId];
  };

  const submitStep = (values) => {
    const progressClone = clone(progress);
    const lastProgressPosition = getLastArrayElement([...progressClone]);
    const oldLastPositionPoint = getLastArrayElement(lastProgressPosition);
    const setStepValues = () => {
      const newStepValuesObject = allStepValues;
      const stepId =
        groupsDictionary[oldLastPositionPoint.group][oldLastPositionPoint.step]
          ?.id;
      newStepValuesObject[stepId] = values;
      setAllStepValues(newStepValuesObject);
      devConsoleLog(stepId, `submitted values:`, values);
    };
    setStepValues();

    let proposedNextProgressPosition = blindlySumAStepToProgressPosition(
      clone(lastProgressPosition)
    );
    let positionPointToAdd = undefined;
    const popProgressPositionUntilNextStepInGroupPositionPointIsValid = () => {
      // TODO check magic inside (what differences this from updateProgressPositionLastPointStepToNextVisibleStepOrPopProgressPosition?)
      if (proposedNextProgressPosition.length) {
        const lastPositionPoint = proposedNextProgressPosition.pop();
        const nextStep = getNextVisibleStepFromPositionPoint(lastPositionPoint);
        positionPointToAdd = { ...lastPositionPoint, step: nextStep };
        if (nextStep === null) {
          if (proposedNextProgressPosition.length) {
            proposedNextProgressPosition = blindlySumAStepToProgressPosition(
              proposedNextProgressPosition
            );
            popProgressPositionUntilNextStepInGroupPositionPointIsValid();
          }
        }
      }
    };
    popProgressPositionUntilNextStepInGroupPositionPointIsValid();

    const stepToAdd = positionPointToAdd.step;
    const groupToAdd = positionPointToAdd?.group;

    if (stepToAdd) {
      proposedNextProgressPosition.push({
        step: stepToAdd,
        group: groupToAdd,
      });
      const updatedProgress = [...progress, proposedNextProgressPosition];
      setProgress(updatedProgress);
      const isCurrentStepAGroup =
        stepToAdd && groupsDictionary[groupToAdd][stepToAdd].isGroup;
      if (!isCurrentStepAGroup)
        updateScreenPathInProcessContext(updatedProgress);
    } else {
      const allValues = getValuesOfCompletedSteps();
      devConsoleLog(`MODULE submitted values:`, allValues);
      submitModule(allValues);
    }
  };

  const manageGroup = (group, stepGroupStepsArray) => {
    const stepsArrayDiffersFromGroupDictionary =
      JSON.stringify(groupsDictionary[group]) !==
      JSON.stringify(stepGroupStepsArray);
    if (stepsArrayDiffersFromGroupDictionary) {
      groupsDictionary[group] = stepGroupStepsArray;
      setGroupsDictionary(groupsDictionary);
    }

    const isNewGroupInProgressPosition =
      !getGroupMatchingPositionPointInLastProgressPosition(group);
    const progressExists = progress && progress.length > 0;
    const initialProgressPositionPositionPoint = {
      step: 0,
      group,
    };
    const newProgress = progressExists
      ? clone(progress)
      : [initialProgressPositionPositionPoint];
    const lastProgressPosition = getLastArrayElement(newProgress);
    const initialProgressPosition = progressExists
      ? [...lastProgressPosition]
      : [];
    let proposedProgressPosition = [...initialProgressPosition];
    let progressChanged = false;

    if (isNewGroupInProgressPosition) {
      proposedProgressPosition.push(initialProgressPositionPositionPoint);
      progressChanged = true;
    }

    if (progressExists) {
      const proposedLastPositionPoint = getLastArrayElement(
        proposedProgressPosition
      );
      const lastPositionPointStep = proposedLastPositionPoint.step;
      const lastPositionPointGroup = proposedLastPositionPoint.group;
      if (!stepInGroupMustShow(lastPositionPointStep, lastPositionPointGroup)) {
        proposedProgressPosition = blindlySumAStepToProgressPosition([
          ...proposedProgressPosition,
        ]);
        progressChanged = true;
      }
    }
    const stepIndex =
      getGroupMatchingPositionPointInLastProgressPosition(group)?.step;

    if (progressChanged) {
      newProgress.pop();
      newProgress.push(getValidProgressPosition(proposedProgressPosition));
      setProgress(newProgress);

      const currentStepForGroupDetection = stepIndex
        ? stepGroupStepsArray[stepIndex]
        : stepGroupStepsArray[0];
      if (!currentStepForGroupDetection.isGroup)
        updateScreenPathInProcessContext(newProgress); // TODO
    }

    return stepGroupStepsArray[stepIndex]?.id;
  };

  const backButtonVisible = progress?.length > 1;

  const getScreenPathFromProgress = (targetProgress) => {
    const lastPosition = getLastArrayElement(targetProgress);
    let screenPath = `${currentModuleObject.id}`;
    lastPosition.forEach((positionPoint) => {
      screenPath += `.${
        groupsDictionary[positionPoint.group][positionPoint.step]?.id
      }`;
    });
    return screenPath;
  };

  const updateScreenPathInProcessContext = (customProgress) => {
    const targetProgress = customProgress || progress;
    const targetProgressIsEmpty = targetProgress && targetProgress.length < 1;
    const screenPath = targetProgressIsEmpty
      ? currentModuleObject.id
      : getScreenPathFromProgress(targetProgress);
    dispatch({
      type: "UPDATE_PROCESS_CONTEXT",
      payload: { screenPath },
    });
    // dispatchEvent({
    //   name: screenPath,
    //   type: EVENT_TYPES.progress,
    //   payload: {
    //     module: currentModuleObject?.id,
    //     screenPath: screenPath,
    //   },
    // });
  };

  return {
    manageGroup,
    submitStep,
    onBack,
    backButtonVisible,
    getStepData,
    allStepValues,
  };
}
